import store from '@/store';
import router from '@/router';
import { fetchRetry } from '@/assets/js/utils';
import { useToast } from 'vue-toast-notification';

class DatastoreConnect {
  constructor(url = '', options = null) {
    this.url = '';
    this.options = options;
    this.requestCache = new Map(); // Cache for deduplicating requests
    this.REQUEST_CACHE_TTL = 500; // 500ms cache TTL for same requests
  }

  // Create a cache key from method and params
  createCacheKey(method, params) {
    return `${method}:${JSON.stringify(params)}`;
  }

  // Check if a cached request is still valid
  getCachedRequest(cacheKey) {
    const cached = this.requestCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.REQUEST_CACHE_TTL) {
      return cached.promise;
    }
    this.requestCache.delete(cacheKey);
    return null;
  }

  // Store a request in cache
  setCachedRequest(cacheKey, promise) {
    this.requestCache.set(cacheKey, {
      promise,
      timestamp: Date.now(),
    });
  }

  static errorMessage(error) {
    if (error === 'INVALID_URL') return ("Datastore URL Not Correct");
    if (error === 'URL_UNAVIALABLE') return ("Datastore Unavailable");
    if (error === 'INVALID_JSON') return ("Datastore Communication Error");
    if (error === "") return 'Datastore Error';
    return error;
  }

  // Reads fe-version/be-version response headers and hard-resets the GUI if either changed
  static checkVersionHeaders(resp) {
    const feVersion = resp.headers.get('fe-version');
    const beVersion = resp.headers.get('be-version');
    if (!feVersion && !beVersion) return;

    const state = store.state.appVersion;
    const feChanged = Boolean(state.feHeaderVersion && feVersion && state.feHeaderVersion !== feVersion);
    const beChanged = Boolean(state.beHeaderVersion && beVersion && state.beHeaderVersion !== beVersion);

    if (feVersion) {
      store.commit('appVersion/setFeHeaderVersion', feVersion);
      store.commit('appVersion/setFrontendVersion', feVersion);
    }
    if (beVersion) {
      store.commit('appVersion/setBeHeaderVersion', beVersion);
      store.commit('appVersion/setVersion', beVersion);
    }

    if (feChanged || beChanged) {
      console.log('[checkVersionHeaders] triggering dialog', {
        feVersion, beVersion, prevFe: state.feHeaderVersion, prevBe: state.beHeaderVersion, feChanged, beChanged,
      });
      store.commit('appVersion/showNewVersionDialog', { frontend: feVersion, backend: beVersion });
    }
  }

  createRPCQuery(url, {
    jsonrpc = "2.0", id = 1, params = {}, method = "", formData = null, token = null, responseType = 'json',
  }, signal = null) {
    const shouldCache = !formData && responseType === 'json';

    // Check cache for non-formData requests
    if (shouldCache) {
      const cacheKey = this.createCacheKey(method, params);
      const cachedPromise = this.getCachedRequest(cacheKey);
      if (cachedPromise) {
        return cachedPromise;
      }
    }

    const promise = new Promise((resolve, reject) => {
      const headers = new Headers();
      // headers.append('Authorization', `Bearer ${store.state.user.token}`);

      let body;
      if (formData) {
        body = formData;
      } else {
        body = JSON.stringify({
          jsonrpc, id, params, method,
        });
        headers.append('Content-Type', "application/json");
      }
      if (responseType === 'blob') {
        headers.append('Accept', 'application/pdf, application/json');
      }

      const options = {
        method: 'POST',
        headers,
        body,
        redirect: 'follow',
      };

      if (token) {
        headers.append('Authorization', `Bearer ${token}`);
      } else {
        options.credentials = 'include';
      }

      if (signal) {
        options.signal = signal;
      }

      let formattedUrl;
      if (process.env.NODE_ENV === 'development') {
        formattedUrl = `${url ? `${url}/` : '/'}api?method=${method}`;
      } else {
        formattedUrl = `/api?method=${method}`;
      }

      const handleResponse = async (resp) => {

        DatastoreConnect.checkVersionHeaders(resp);
        if (responseType === 'blob' && !resp.headers.get('content-type')?.includes('application/json')) {
          if (!resp.ok) {
            throw new Error(`Request failed with status ${resp.status}`);
          }
          resolve(await resp.blob());
          return;
        }
        const json = await resp.json();
        if (json.error?.code === 401) {
          router.push('/logout'); // Redirect to home
          return reject(Error("Unauthorized (401)"));
        }
        if (json.error?.code === 403) {
          const $toast = useToast();
          $toast.open({
            queue: true,
            duration: 5000,
            message: json.error.message,
            type: 'error',
            position: 'top',
          });
          reject(json);
        }
        resolve(json);
      };

      if (this.options?.tries) {
        const retryOptions = { delay: this.options.delay, tries: this.options.tries, backoff: this.options.backoff };
        fetchRetry(formattedUrl, retryOptions, options)
          .then(handleResponse)
          .catch((error) => reject(DatastoreConnect.errorMessage(error)));
      } else {
        fetch(formattedUrl, options)
          .then(handleResponse)
          .catch((error) => reject(DatastoreConnect.errorMessage(error)));
      }
    });

    // Cache the promise for non-formData requests
    if (shouldCache) {
      const cacheKey = this.createCacheKey(method, params);
      this.setCachedRequest(cacheKey, promise);
    }

    return promise;
  }

  fetchWithAuth(url, { method = 'POST', params = {}, formData = null } = {}, signal = null) {
    return new Promise((resolve, reject) => {
      const headers = new Headers();
      // headers.append('Authorization', `Bearer ${store.state.user.token}`);

      let body;
      if (formData) {
        body = formData;
      } else {
        body = JSON.stringify({
          params,
        });
        headers.append('Content-Type', "application/json");
      }

      const options = {
        method,
        headers,
        body,
        redirect: 'follow',
        credentials: "include",
      };

      if (signal) {
        options.signal = signal;
      }

      // let formattedUrl;
      // if (process.env.NODE_ENV === 'development') {
      //   formattedUrl = `${url ? `${url}/` : ''}api?method=${method}`;
      // } else {
      //   formattedUrl = `api?method=${method}`;
      // }

      const handleResponse = async (response) => {
        if (!response.ok) {
          if (response.status === 401) {
            router.push('/'); // Redirect to login
            throw new Error("Unauthorized (401)");
          }
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
      
        DatastoreConnect.checkVersionHeaders(response);
        resolve(response);
      };

      if (this.options?.tries) {
        const retryOptions = { delay: this.options.delay, tries: this.options.tries, backoff: this.options.backoff };
        fetchRetry(url, retryOptions, options)
          .then(handleResponse)
          .catch((error) => reject(DatastoreConnect.errorMessage(error)));
      } else {
        fetch(url, options)
          .then(handleResponse)
          .catch((error) => reject(DatastoreConnect.errorMessage(error)));
      }
    });
  }

  // ----------------------------------------   Package   ----------------------------------------------

  async getPackageList(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "package.list", params });
  }

  async addPackage(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "package.add", params });
  }

  async updatePackageQuota(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "package.update_quota", params });
  }

  async getPackage(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "package.get", params });
  }

  async deletePackage(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "package.delete", params });
  }

  // ----------------------------------------   Accounting   ----------------------------------------------
  async invoiceUpdate(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "invoice.update", params });
  }
 
  async checkImageLimitForAccounting(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "accounting.check_image_limit", params });
  }

  async getAccountingMaxImageSize(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "accounting.get_single_image_size", params });
  }

  async getCostPackage(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "billing.get_cost_package", params });
  }

  async getCostPackageByName(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "billing.get_cost_package_name", params });
  }

  async getUsageSummaryForOrganization(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "accounting.get_usage_summary", params });
  }

  async getDateRangeReport(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "accounting.date_range_report", params });
  }

  async getDetailedDatasets(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "accounting.get_dataset_summary", params });
  }

  async getDetailedImages(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "accounting.get_image_summary", params });
  }

  async addFunds(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "org.addfunds", params });
  }

  async requestFunds(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "funds.request", params });
  }

  async generateMonthlyBill(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "billing.generate_monthly_bill", params });
  }

  async retrieveMonthlyBill(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "billing.get_monthly_bill", params });
  }

  async retrieveAnnualBill(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "billing.annual_bills", params });
  }

  async getAllInstances(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "billing.get_all_instances", params });
  }

  async getRecentBills(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "billing.list_billings", params });
  }

  async checkQuota(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "billing.check_quota", params });
  }

  // ----------------------------------------   Projects   ----------------------------------------------

  async getProject(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "project.get", params });
  }

  async getProjectList(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "project.list", params });
  }

  async getProjectFilteredList(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "project.filter", params });
  }

  async addProject(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "project.add", params });
  }

  async deleteProject(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "project.del", params });
  }

  async updateProject(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "project.update", params });
  }

  async cloneProject(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "project.clone", params });
  }

  async getProjectAccessList(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "projectaccess.list", params });
  }

  async getProjectAccessForSingleUser(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "projectaccess.get_for_user", params });
  }

  async addProjectAccess(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "projectaccess.add", params });
  }

  async updateProjectAccess(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "projectaccess.update", params });
  }

  async deleteProjectAccess(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "projectaccess.del", params });
  }

  async revertProjectAccessToDefault(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "projectaccess.revert_to_default", params });
  }

  async setProjectAccessToPrivate(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "projectaccess.set_to_private", params });
  }

  async getOrganizationDefaultPermissions(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "projectaccess.get_org_default", params });
  }

  async setProjectAccessToPublic(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "projectaccess.set_to_public", params });
  }

  // ----------------------------------------   Datasets   ----------------------------------------------

  async getDatasetList(params) {
    return this.createRPCQuery(`${this.url}`, { method: "dataset.list", params });
  }

  async getDatasetById(params) {
    return this.createRPCQuery(`${this.url}`, { method: "dataset.get", params });
  }

  async createDataset(params) {
    return this.createRPCQuery(`${this.url}`, { method: "dataset.create", params });
  }

  async deleteDataset(params) {
    return this.createRPCQuery(`${this.url}`, { method: "dataset.delete", params });
  }

  async getDatasetStats(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "dataset.stats", params });
  }

  async updateDataset(params) {
    return this.createRPCQuery(`${this.url}`, { method: "dataset.update", params });
  }

  async getDatasetDownloads(params) {
    return this.createRPCQuery(`${this.url}`, { method: "dataset.downloads", params });
  }

  async parkDataset(params) {
    return this.createRPCQuery(`${this.url}`, { method: "dataset.park", params });
  }

  async unparkDataset(params) {
    return this.createRPCQuery(`${this.url}`, { method: "dataset.unpark", params });
  }

  async getDatasetImageSources(params) {
    return this.createRPCQuery(`${this.url}`, { method: "dataset.get_sources", params });
  }

  async getDatasetAnnotationTypes(params) {
    return this.createRPCQuery(`${this.url}`, { method: "dataset.get_annotation_types", params });
  }

  async getDatasetDateRange(params) {
    return this.createRPCQuery(`${this.url}`, { method: "dataset.date_range", params });
  }

  async getDatasetTopics(params) {
    return this.createRPCQuery(`${this.url}`, { method: "dataset.get_topics", params });
  }

  async copySequences(params) {
    return this.createRPCQuery(`${this.url}`, { method: "dataset.copy_sequences", params });
  }

  async confirmCopySequences(params) {
    return this.createRPCQuery(`${this.url}`, { method: "dataset.confirm_copy_sequences", params });
  }

  async getParkedDatasetCount(params) {
    return this.createRPCQuery(`${this.url}`, { method: "dataset.get_parked_count", params });
  }

  // ----------------------------------------   Dataset Access   --------------------------------------

  async getDatasetAccessList(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "datasetaccess.list", params });
  }

  async addDatasetAccess(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "datasetaccess.add", params });
  }

  async updateDatasetAccess(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "datasetaccess.update", params });
  }

  async deleteDatasetAccess(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "datasetaccess.del", params });
  }

  async getDatasetAccessUserList(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "datasetaccess.user_list", params });
  }

  async getProjectDatasetAccessUserList(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "datasetaccess.project_user_list", params });
  }

  async getDatasetAccessForSingleUser(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "datasetaccess.get_for_user", params });
  }

  async revertDatasetAccessToDefault(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "datasetaccess.revert_to_default", params });
  }

  async setDatasetAccessToPrivate(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "datasetaccess.set_to_private", params });
  }

  // ----------------------------------------   Images   ----------------------------------------------

  async getImage(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "image.get", params }, signal);
  }

  async deleteImageFromDataset(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "image.delete_from_dataset", params });
  }

  async getFilteredImages(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "image.list.filter", params }, signal);
  }

  async deleteFilteredImages(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "image.delete.filter", params }, signal);
  }

  async deleteFilteredImages2(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "image.delete.filter.v2", params }, signal);
  }

  async copyImages(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "image.copy_images", params }, signal);
  }

  async setImageGroupId(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "image.set_group_id", params }, signal);
  }

  // ----------------------------------------   Annotations   ----------------------------------------------

  async addAnnotations(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "annotation.add", params });
  }

  async addAnnotationsBulk(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "annotation.add_bulk", params });
  }

  async getAnnotations(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "annotation.list2", params });
  }

  async updateAnnotations(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "annotation.update", params });
  }

  async deleteAnnotations(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "annotation.del", params });
  }

  async getAnnotationJson(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "annotation.data_json", params });
  }

  async deleteAnnotationBulk(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "annotation.bulk.del", params });
  }

  async deleteAnnotationsByTrackingId(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "annotation.delete_tracking_id", params });
  }

  async getAnnotationFile(annotation_id, dataType = 'text', signal = null) {
    const headers = new Headers();
    headers.append('Authorization', `Bearer ${store.state.user.token}`);
    const requestOptions = {
      method: 'GET',
      redirect: 'follow',
      headers,
    };
    return fetch(`/annotation/${annotation_id}`, requestOptions, signal)
      .then(async (resp) => {
        if (!resp.ok) {
          throw Error(await resp.text());
        }
        if (dataType === 'blob') {
          return resp.blob();
        }
        if (dataType === 'arrayBuffer') {
          return resp.arrayBuffer();
        }
        return resp.text();
      });
  }

  async getSensorFile(sensor_id, dataType = 'text', signal = null) {
    const headers = new Headers();
    headers.append('Authorization', `Bearer ${store.state.user.token}`);
    const requestOptions = {
      method: 'GET',
      redirect: 'follow',
      headers,
    };
    return fetch(`/sensor/${sensor_id}`, requestOptions, signal)
      .then(async (resp) => {
        if (!resp.ok) {
          throw Error(await resp.text());
        }
        if (dataType === 'blob') {
          return resp.blob();
        }
        if (dataType === 'arrayBuffer') {
          return resp.arrayBuffer();
        }
        return resp.text();
      });
  }

  async updateAnnotationFile(annotation_id, file, signal = null) {
    const headers = new Headers();
    headers.append('Authorization', `Bearer ${store.state.user.token}`);
    const requestOptions = {
      method: 'PUT',
      redirect: 'follow',
      headers,
      body: file,
      credentials: "include",
    };
    return fetch(`/annotation/${annotation_id}`, requestOptions, signal)
      .then(async (resp) => {
        if (!resp.ok) {
          throw Error(await resp.text());
        }
        return resp.text();
      });
  }

  // ----------------------------------------   Annotation Sets   ----------------------------------------------

  async createAnnotationSet(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "annset.add", params });
  }

  async updateAnnotationSet(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "annset.update", params });
  }

  async getAnnotationSets(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "annset.list", params }, signal);
  }

  async getAnnotationCounts(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "annset.counts", params }, signal);
  }

  async getAnnotationSet(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "annset.get", params });
  }

  async copyAnnotationSet(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "annset.copy", params });
  }

  async deleteAnnotationSet(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "annset.del", params });
  }

  async getAnnotationSetConfusionMatrix(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "annset.confusion_matrix", params });
  }

  async getAnnotationsForAnnotationSets(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "annset.get_all_annotations", params });
  }

  // ----------------------------------------   Labels   ----------------------------------------------

  async getLabelList(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "label.list", params });
  }

  async addLabel(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "label.add", params });
  }

  async addLabel2(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "label.add2", params });
  }

  async deleteLabel(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "label.del", params });
  }

  async updateLabel(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "label.update", params });
  }

  async mergeLabel(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "label.merge", params });
  }

  // ----------------------------------------   Auto Annotations   ----------------------------------------------

  async getAutoAnnList(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "autoann.list", params });
  }

  async getAutoAnnSet(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "autoann.get", params });
  }

  async startAutoAnn(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "autoann.start", params });
  }

  async deleteAutoAnn(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "autoann.del", params });
  }

  async terminateAutoAnn(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "autoann.terminate", params });
  }

  // ----------------------------------------   Auto Segmentations   ----------------------------------------------

  async startAutoSegmentation(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "autoseg.start", params });
  }

  async getAutoSegList(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "autoseg.list", params });
  }

  async getAutoSeg(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "autoseg.get", params });
  }

  async removeAutoSeg(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "autoseg.del", params });
  }

  // ----------------------------------------   Reviews   ----------------------------------------------

  async addReviews(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "review.add", params });
  }

  async getReviewImages(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "review.get_images", params });
  }

  async getLabellingImages(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "review.get_labelling_images", params });
  }

  async getLabellingSequenceFrames(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "review.get_labelling_sequence_frames", params }, signal);
  }

  async setImageReviewStatusDone(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "imagereviewstatus.update_done", params });
  }

  async deleteImageReviewStatus(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "imagereviewstatus.del", params });
  }

  // ----------------------------------------   Review Tasks   ----------------------------------------------

  async addReviewTask(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "reviewtask.add", params });
  }

  async deleteReviewTask(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "reviewtask.del", params });
  }

  async getReviewTasks(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "reviewtask.list", params });
  }

  async getReviewTaskById(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "reviewtask.get", params });
  }

  async getReviewerList(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "reviewtask.reviewer_list", params });
  }

  async getReviewTaskRemainingCount(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "reviewtask.remaining_count", params });
  }

  async getLabelingTaskRemainingCount(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "reviewtask.labeling_remaining_count", params });
  }

  async getTaskDoneImagesByUser(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "reviewtask.done_images_by_user", params });
  }

  async deleteReviewTaskUser(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "reviewtaskuser.del", params });
  }

  async addReviewTaskUser(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "reviewtaskuser.add", params });
  }

  async submitAuditManualAnnotationTask(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "manualannotationtask.audit.submit", params });
  }

  async submitKeyframes(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "manualannotationtask.keyframe.submit", params });
  }

  async getReviewTaskImageList(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "reviewtask.image_list", params });
  }

  // ----------------------------------------   Review Task Access   --------------------------------------

  async getReviewTaskAccessList(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "reviewaccess.list", params });
  }

  async addReviewTaskAccess(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "reviewaccess.add", params });
  }

  async updateReviewTaskAccess(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "reviewaccess.update", params });
  }

  async deleteReviewTaskAccess(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "reviewaccess.del", params });
  }

  async getReviewTaskAccessForSingleUser(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "reviewaccess.get_for_user", params });
  }

  async revertReviewTaskAccessToDefault(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "reviewaccess.revert_to_default", params });
  }

  // ----------------------------------------   Review Sessions   ----------------------------------------------

  async addReviewSession(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "reviewsession.add", params });
  }

  // ----------------------------------------   S3   ----------------------------------------------

  async s3CreatePresignedUrl(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "s3.create_presigned_url", params });
  }

  async s3SingleImageImport(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "s3.single_image_import", params });
  }

  // ----------------------------------------   Trainer   ----------------------------------------------

  async addTrainer(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "trainer.add", params });
  }

  async updateTrainer(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "trainer.update", params });
  }

  async deleteTrainer(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "trainer.del", params });
  }

  async getExperimentList(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "experiment.list", params });
  }

  async getExperiment(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "trainer.get", params });
  }

  async getTrainerData(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "trainer.getdata", params });
  }

  async startTrainer(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "trainer.server.start", params });
  }

  async stopTrainer(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "trainer.server.stop", params });
  }

  async updateEC2Status(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "trainer.server.update_ec2", params });
  }

  async getTrainerStatus(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "trainer.status", params });
  }

  async terminateTrainingSessions(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "trainer.session.terminate", params });
  }

  async deleteTrainingSession(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "trainer.session.delete", params });
  }

   async resumeTrainingSession(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "trainer.session.resume", params });
  }

  async getTrainingSession(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "trainer.session.get", params });
  }

  async getTrainingSessionList(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "trainer.session.list", params });
  }

  async getTrainingSessionsWithFilter(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "trainer.session.filter", params });
  }

  async getTrainingSessionLogs(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "trainer.session.logs", params });
  }

  async updateTrainingSession(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "trainer.session.update", params });
  }

  async updateValidationSession(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "validate.session.update", params });
  }

  async generateTwoStageModel(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "trainer.server.generate_two_stage", params });
  }

  async getTrainerParametersSchema(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "trainer.server.schema", params });
  }

  async getTrainingSessionArtifacts(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "trainer.get_artifacts", params });
  }

  async getModelInfo(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "trainer.model.info", params });
  }

  // ----------------------------------------   Authentication   ----------------------------------------------

  async signIn(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "auth.login", params });
  }

  async logout(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "auth.logout", params });
  }

  async refreshToken(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "auth.refresh", params });
  }

  async verifyToken(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "auth.verify_token", params });
  }

  async send2FA(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "auth.send2fa", params });
  }

  async generateBridgeToken(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "auth.generate_bridge_token", params });
  }

  /**
   * Start an OAuth authorization-code flow.
   * @param {{ provider: 'google' | 'microsoft' }} params
   * @returns {Promise<{ result: { provider: string, auth_url: string, state: string } }>}
   */
  async oauthStart(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "auth.oauth_start", params });
  }

  /**
   * Complete an OAuth authorization-code flow after provider redirect.
   * @param {{ provider: string, code: string, state: string }} params
   * @returns {Promise<{ result: import('../auth/oauth-service').OAuthFinishResult }>}
   */
  async oauthFinish(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "auth.oauth_finish", params });
  }

  // ----------------------------------------   User   ----------------------------------------------

  async createUser(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "user.add", params });
  }

  async updateUser(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "user.update", params });
  }

  async updateUserPassword(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "user.update_password_with_auth", params });
  }

  async resetUserPassword(params = {}) {
    const token = params.token;
    delete params.token;
    return this.createRPCQuery(`${this.url}`, { method: "user.update_password_with_auth", params, token });
  }

  async updateUserPasswordAuth(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "user.update_password_with_auth", params });
  }

  async getAllRoles(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "user.get_all_roles", params });
  }

  async getUserList(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "user.list", params });
  }

  async getUserListByParent(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "user.list_by_parent", params });
  }

  async getUserListByOrganization(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "user.list_by_org", params });
  }

  async deleteUser(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "user.del", params });
  }

  async checkForUser(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "user.check_for_user", params });
  }

  async getUserBucket(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "user.get_bucket", params });
  }

  async registerNewUser(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "user.register", params });
  }

  async sendFeedback(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "user.feedback", params });
  }

  async generateUserAvatar(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "user.avatar", params });
  }

  async userContact(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "user.contact", params });
  }

  async userRequestFunds(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "user.request_funds", params });
  }

  async userForgotPassword(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "user.forgot_password", params });
  }

  async listUsersMatchingUsernameOrEmail(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "user.list_matching", params });
  }

  // ----------------------------------------   Organization   ----------------------------------------------

  async createOrg(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "org.add", params });
  }

  async getOrgList(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "org.list", params });
  }

  async getOrg(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "org.get", params });
  }

  async updateOrg(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "org.update", params });
  }

  async getOrgDataField(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "org.get_data_field", params });
  }

  async setOrgDataField(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "org.set_data_field", params });
  }

  async listWarnings(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "org.list_warnings", params });
  }

  // ----------------------------------------   Import   ----------------------------------------------

  async getSupportedImportTypes(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "import.types", params });
  }

  // ----------------------------------------   Export   ----------------------------------------------

  async startExport(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "export.start", params });
  }

  async getExportList(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "export.list", params });
  }

  async getExportStatus(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "export.status", params });
  }

  async deleteExport(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "export.delete", params });
  }

  // ----------------------------------------   Groups   ----------------------------------------------

  async getGroupListForDataset(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "groups.list", params });
  }

  async createGroupsForDataset(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "groups.create", params });
  }

  async updateGroup(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "groups.update", params });
  }

  async deleteGroup(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "groups.delete", params });
  }

  // -----------------------------------   Training Sets (Removed in 2.4)   ----------------------------------------------

  async getTrainingSetList(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "trainset.list", params });
  }

  async getTrainingSet(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "trainset.get", params });
  }

  async addTrainingSet(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "trainset.add", params });
  }

  async deleteTrainingSet(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "trainset.delete", params });
  }

  async updateTrainingSet(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "trainset.update", params });
  }

  async getTrainingSetConversionList(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "trainset.conv.list", params });
  }

  async addTrainingSetConversion(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "trainset.conv.add", params });
  }

  async deleteTrainingSetConversion(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "trainset.conv.del", params });
  }

  async updateTrainingSetConversion(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "trainset.conv.update", params });
  }

  async getTrainingSetCacheList(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "trainset.cache.list", params });
  }

  async addTrainingSetCache(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "trainset.cache.add", params });
  }

  async deleteTrainingSetCache(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "trainset.cache.del", params });
  }

  async updateTrainingSetCache(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "trainset.cache.update", params });
  }

  async getDockerTasksDashboard(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "docker.list.dashboard", params });
  }

  async getDockerTaskById(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "docker.get", params });
  }

  async addDockerTask(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "docker.add", params });
  }

  async deleteDockerTask(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "docker.delete", params });
  }

  async updateDockerTask(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "docker.update", params });
  }

  async updateDockerTaskStatus(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "docker.update.status", params });
  }

  async updateTaskStage(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "status.update", params });
  }

  async addTask(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "task.add", params });
  }

  async listTasks(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "task.list", params });
  }

  async taskAppParams(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "task.app.params", params });
  }

  // ----------------------------------------   Graph   ----------------------------------------------

  async startGraph(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "graph.start", params });
  }

  async getCharts(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "graph.get", params });
  }

  async stopGraph(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "graph.stop", params });
  }

  // ----------------------------------------   Validator   ----------------------------------------------

  async validate(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "validate.start", params });
  }

  async getValidateSessionList(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "validate.session.list", params });
  }

  async getValidateSessionListByTrainingSession(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "validate.session.list_by_train", params });
  }

  async getValidateSessionListByExperiment(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "validate.session.list_by_experiment", params });
  }

  async getValidateGraphs(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "validate.graph.get", params });
  }

  async getValidateSession(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "validate.session.get", params });
  }

  async terminateValidateSessions(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "validate.session.terminate", params });
  }

  async deleteValidateSessions(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "validate.session.delete", params });
  }

  async validateUpdateEC2(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "validate.session.update_ec2", params });
  }

  async getValidatorSchema(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "validate.schema", params });
  }

  async getValidateSessionLogs(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "validate.session.logs", params });
  }

  // ----------------------------------------   Video Import   -----------------------------------------

  async videoImport(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "video.import", params });
  }

  // ----------------------------------------   Upload   -----------------------------------------

  async addUploadData(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "upload.image.add", params });
  }

  async uploadAnnotations(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "upload.annotation", params });
  }

  async completeUpload(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "upload.complete", params });
  }

  async uploadVideo(formData = null) {
    return this.createRPCQuery(`${this.url}`, { method: "upload.video", formData });
  }

  // ----------------------------------------   Stats   -----------------------------------------

  async statsCompare(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "stats.compare", params }, signal);
  }

  // ----------------------------------------   Version   ----------------------------------------------

  async getAppVersion() {
    return this.createRPCQuery(`${this.url}`, { method: "version" });
  }

  async getAppsSummary(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "apps.summary", params });
  }

  // ----------------------------------------   Organization   ----------------------------------------------

  async getConverterOptions(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "convert.list", params });
  }

  async convertModel(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "convert.start", params });
  }

  async convertorStatus(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "convert.status", params });
  }

  async convertorFindAllFiles(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "convert.allfiles", params });
  }

  async convertorGetDockerTasks(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "convert.dockerlist", params });
  }

  // ----------------------------------------   Recycle   ----------------------------------------------

  async getListOfRecycledItems(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "recycle.list", params }, signal);
  }

  async undeleteRecycledItem(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "recycle.undel", params });
  }

  async purgeRecycledItem(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "recycle.purge", params });
  }

  // ----------------------------------------   Permission   ----------------------------------------------

  async getUserPermissions(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "permission.get_for_user", params }, signal);
  }

  // ----------------------------------------   Sequences   ----------------------------------------------

  async getSequences(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "sequences.list", params }, signal);
  }

  async purgeSequence(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "sequences.purge", params }, signal);
  }

  async updateSequenceDescription(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "sequence.update", params }, signal);
  }

  // --------------------------------------  Cloud Instances   ------------------------------------------

  async startCloudInstance(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "cloud.server.start", params }, signal);
  }

  async listCloudInstances(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "cloud.server.list", params }, signal);
  }

  async stopCloudInstance(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "cloud.server.del", params }, signal);
  }

  async stopMultipleCloudInstance(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "cloud.server.del_multiple", params }, signal);
  }

  async getCloudInstanceStatus(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "cloud.server.get", params }, signal);
  }

  // ----------------------------------------   Tags   ----------------------------------------------

  async listTagsForDataset(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "tags.list_dataset", params }, signal);
  }

  async listTagsForImages(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "tags.list_images", params }, signal);
  }

  async updateTagName(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "tags.update", params }, signal);
  }

  async addNewTagForImages(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "tags.add_images", params }, signal);
  }

  async addNewTagForDataset(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "tags.add_dataset", params }, signal);
  }

  async removeTagForImages(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "tags.del_image", params }, signal);
  }

  async removeTagForDataset(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "tags.del_dataset", params }, signal);
  }

  // ----------------------------------------   Snapshots   ----------------------------------------------

  async listSnapshots(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "snapshots.list", params }, signal);
  }

  async filterSnapshots(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "snapshots.filter", params }, signal);
  }

  async createNewSnapshot(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "snapshots.create", params }, signal);
  }

  async restoreSnapshot(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "snapshots.restore", params }, signal);
  }

  async removeSnapshot(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "snapshots.delete", params }, signal);
  }

  async updateSnapshot(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "snapshots.update", params }, signal);
  }

  async createSnapshotUploadUrl(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "snapshots.create_upload_url", params }, signal);
  }

  async createSnapshotDownloadUrl(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "snapshots.create_download_url", params }, signal);
  }

  async createSnapshotUploadMultipartUrl(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "snapshots.create_upload_url_multipart", params }, signal);
  }

  async completeSnapshotUploadMultipartUrl(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "snapshots.complete_multipart_upload", params }, signal);
  }

  async getSnapshotsSummary(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "snapshots.summary", params }, signal);
  }

  // ----------------------------------------   Hash   ----------------------------------------------

  async GetDatasetAnnotationsHash(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "dataset.hash", params }, signal);
  }

  // ----------------------------------------   Time   ----------------------------------------------

  async getCurrentTime(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "time.now", params }, signal);
  }

  async getElapsedTime(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "time.elapsed", params }, signal);
  }

  // ----------------------------------------   Coupons   ----------------------------------------------

  async addCoupon(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "coupons.add", params }, signal);
  }

  async listCoupons(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "coupons.list", params }, signal);
  }

  async useCoupons(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "coupons.use", params }, signal);
  }

  // ----------------------------------------  Generic API Call   ----------------------------------------------

  async api(method = '', params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method, params }, signal);
  }

  async apiBlob(method = '', params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method, params, responseType: 'blob' }, signal);
  }
  // ----------------------------------------   Payment Gateway   ----------------------------------------------

  async defaultCard(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "payment.default_card", params }, signal);
  }

  async deleteCard(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "payment.delete_card", params }, signal);
  }

  async paymentSessionSuccess(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "payment.session_success", params }, signal);
  }

  async setupCard(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "payment.setup", params }, signal);
  }

  async paymentSubscribe(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "payment.subscribe", params }, signal);
  }

  async getPaymentInfo(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "payment.info", params }, signal);
  }

  async topupFunds(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "payment.topup", params }, signal);
  }

  async listPlans(params = {}, signal = null) {
    return this.createRPCQuery(`${this.url}`, { method: "plans.list", params }, signal);
  }



   async purchaseCredits(params = {}) {
    return this.createRPCQuery(`${this.url}`, { method: "billing.purchase_credits", params });
  }

    async uploadGetUrl(params = null) {
    return this.createRPCQuery(`${this.url}`, { method: "upload.get.url", params });
  }



  // Upload Resource Image
  async UploadResourceImage(params = {}, signal = null) {
    const url = `${this.url}/resource/image?type=project&id=${params.project_id}`;

    if (params.image === null) {
      return;
    }

    const formData = new FormData();
    formData.append('file', params.image);

    return this.fetchWithAuth(url, { formData });
  }
}

class LogsConnect {
  constructor() {
    if (process.env.VUE_APP_BACKEND_SOCKET_URL) {
      // Explicit socket URL (serve / serve-local / serve-custom).
      const port = process.env.VUE_APP_BACKEND_PORT || 80;
      this.baseUrl = `${process.env.VUE_APP_BACKEND_SOCKET_URL}:${port}`;
    } else {
      // Gateway mode (serve-test): same-origin so the dev-server proxies the ws
      // (works from localhost and a LAN IP / phone).
      const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
      this.baseUrl = `${proto}://${window.location.host}`;
    }
  }

  // Get WebSocket URL for a specific endpoint
  getUrl(endpoint = '') {
    return `${this.baseUrl}${endpoint}`;
  }

  // Connect to WebSocket endpoint
  connect(endpoint = '') {
    return new Promise((resolve, reject) => {
      try {
        const wsUrl = this.getUrl(endpoint);
        const ws = new WebSocket(wsUrl);
        
        ws.onopen = () => {
          resolve(ws);
        };

        ws.onerror = (error) => {
          reject(error);
        };
      } catch (error) {
        reject(error);
      }
    });
  }
}

export default DatastoreConnect;
export { LogsConnect };
